import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { Grid, Typography, TextField, Button, Stack, MenuItem } from '@mui/material';

import Layout from '../components/Layout/Layout';
import { getSystemConfigList, updateSystemConfig } from '../network/apis';

function ReservationConfiguration() {
  const { t } = useTranslation();
  const [systemConfig, setSystemConfig] = useState([]);

  const getSystemConfigListApi = () => {
    getSystemConfigList().then((res) => {
      setSystemConfig(res.data?.content || []);
    });
  };

  useEffect(() => {
    getSystemConfigListApi();
  }, []);

  const initialValues = systemConfig.reduce((acc, item) => {
    acc[item.code] = item.value;
    acc[`${item.code}_TYPE`] = item.type;
    return acc;
  }, {});

  const validationSchema = Yup.object(
    systemConfig.reduce((acc, item) => {
      if (item.type === 'LABEL') {
        acc[item.code] = Yup.string().required(t('General.required_field'));
      } else {
        acc[item.code] = Yup.number()
          .typeError(t('Validation.wrongNumberFormat'))
          .required(t('General.required_field'))
          .min(0, t('Validation.min_zero'));
      }

      return acc;
    }, {})
  );

  return (
    <>
      <Helmet>
        <title>{t('manageApplication.reservationConfiguration')}</title>
      </Helmet>

      <Layout>
        {systemConfig.length > 0 && (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              try {
                await Promise.all(
                  systemConfig.map((item) =>
                    updateSystemConfig({
                      code: item.code,
                      type: item.type === 'LABEL' ? 'LABEL' : values[`${item.code}_TYPE`],
                      value: item.type === 'LABEL' ? values[item.code] : Number(values[item.code]),
                    })
                  )
                );

                toast.success(t('General.updatedSuccessfully'));
                getSystemConfigListApi();
              } catch (error) {
                toast.error(error?.response?.MESSAGE);
                console.error(error);
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {systemConfig.map((item) => (
                    <Grid item xs={12} md={6} key={item.code}>
                      <Typography variant="body1" gutterBottom>
                        {t(`ReservationConfiguration.${item.code}`)}
                      </Typography>

                      {item.type === 'LABEL' ? (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="text"
                            name={item.code}
                            value={values[item.code]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched[item.code] && Boolean(errors[item.code])}
                            helperText={touched[item.code] && errors[item.code]}
                          />
                        </Grid>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={8}>
                            <TextField
                              fullWidth
                              type="number"
                              name={item.code}
                              value={values[item.code]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched[item.code] && Boolean(errors[item.code])}
                              helperText={touched[item.code] && errors[item.code]}
                            />
                          </Grid>

                          <Grid item xs={4}>
                            <TextField
                              select
                              fullWidth
                              name={`${item.code}_TYPE`}
                              value={values[`${item.code}_TYPE`]}
                              onChange={handleChange}
                            >
                              <MenuItem value="DAY">{t('General.days')}</MenuItem>
                              <MenuItem value="HOUR">{t('General.hours')}</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button variant="contained" type="submit">
                        {t('RquestsAndConfigratrions.update')}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        )}
      </Layout>
    </>
  );
}

export default ReservationConfiguration;
